import axios from 'axios'
import http, {
  videoAiInstance,
  questionAnswerAiInstance,
  draftVideoInstance,
  videoCheckInstance
} from './http'
import { config } from '../../constants'

function changeMachineState(state) {
  return videoAiInstance({
    method: 'GET',
    url: `/default/video-generation-comfy?machine_state=${state}`
  })
}

function generateVideo(data) {
  return videoAiInstance({
    method: 'POST',
    url: '/default/video-generation-comfy',
    data
  })
}

function generateShots(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/shots-generation-v2',
    data
  })
}

function mapCharacterToShots(data) {
  return questionAnswerAiInstance({
    method: 'POST',
    url: '?service=character-mapping',
    data
  })
}

function groupShotsToScenes(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/cluster-scenes',
    data
  })
}

function generateVideoStory(data) {
  return questionAnswerAiInstance({
    method: 'POST',
    url: '?service=video-story-generation',
    data
  })
}

function getAvailableCharacters(data = {}) {
  return draftVideoInstance({
    method: 'POST',
    url: '/list-exsisting-characters',
    data
  })
}

function createCharacter(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/generate-character',
    data
  })
}

function generateImageDeepAI(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/deepai-image-generate',
    data
  })
}

function generateImageCraiyon(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/craiyon-character-image-generate',
    data
  })
}

function editImageDeepAI(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/deepai-image-edit',
    data
  })
}

function generateLumaVideo(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/luma-video-generate',
    data
  })
}

function generateTransition(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/video-transitions',
    data
  })
}

function generateVideoDeepAI(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/deepai-generate',
    data
  })
}

function getTaskStatus(id) {
  return draftVideoInstance({
    method: 'GET',
    url: `/status/${id}`
  })
}

// Get AI Videos with pagination
function getAiVideos(offset = 0, limit = 5) {
  return http({
    method: 'GET',
    url: `/ai-videos?offset=${offset}&limit=${limit}`
  })
}

// Get AI Video by ID
function getAiVideoById(id) {
  console.log(`Get AI Video by ID: ${id}`)
  return http({
    method: 'GET',
    url: `/ai-videos/${id}`
  })
}

// Create AI Video
function createAiVideo(data) {
  return http({
    method: 'POST',
    url: '/ai-videos',
    data
  })
}

// Update AI Video
function updateAiVideo(id, data) {
  return http({
    method: 'PUT',
    url: `/ai-videos/${id}`,
    data
  })
}

// Delete AI Video
function deleteAiVideo(id) {
  return http({
    method: 'DELETE',
    url: `/ai-videos/${id}`
  })
}

function generateLocationReference(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/location-refrence-generator',
    data
  })
}

function generatePropReference(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/props-reference-generator',
    data
  })
}

function listOutfits(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/list-outfits',
    data
  })
}

// TTS Enhanced API - List Available Voices
function listAIVoices(videoId) {
  return draftVideoInstance({
    method: 'POST',
    url: '/tts-enhanced-list',
    data: {
      action: 'list_voices',
      include_metadata: true,
      video_id: videoId
    }
  })
}

// TTS Enhanced API - Generate Single Voice Audio
function generateAudio(data) {
  // Expected data format:
  // {
  //   voice_id: string,
  //   text: string,
  //   speed_factor?: number (0.5-2.0, default 1.0),
  //   exaggeration?: number (0.1-2.0, default 1.0),
  //   cfg_weight?: number (0.0-1.0, default 0.8),
  //   pause_duration?: number (0.0-2.0, default 0.0),
  //   use_emotion_matching?: boolean (default false)
  // }
  return draftVideoInstance({
    method: 'POST',
    url: '/tts-enhanced',
    data: {
      action: 'generate_audio',
      ...data
    }
  })
}

// TTS Enhanced API - Generate Dialogue
function generateDialogue(data) {
  // Expected data format:
  // {
  //   turns: array of [voice_id, text] pairs,
  //   silence_duration?: number (0.0-2.0, default 0.5),
  //   global_speed_factor?: number (0.5-2.0, default 1.0)
  // }
  return draftVideoInstance({
    method: 'POST',
    url: '/tts-enhanced',
    data: {
      action: 'generate_dialogue',
      ...data
    }
  })
}

function regenerateVoiceover(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/tts-enhanced',
    data: {
      action: 'regenerate_scene_audio',
      ...data
    }
  })
}

// TTS Enhanced API - Upload Custom Voice
async function uploadCustomVoice(formData) {
  // formData should contain:
  // - voice_file: File (WAV, MP3, OGG, M4A, FLAC)
  // - voice_name: string (alphanumeric)
  // - description?: string
  // - overwrite?: string

  console.log('uploadCustomVoice called with formData:', formData)

  // Use axios.post directly without setting Content-Type
  // Axios will automatically detect FormData and set multipart/form-data with boundary
  try {
    const response = await axios.post(
      `${config.videoGenerationUrl}/voice/upload`,
      formData
      // NO headers - let axios auto-detect FormData
    )
    console.log('Upload successful:', response)
    return response
  } catch (error) {
    console.error('Upload error:', error)
    throw error
  }
}

function generateSceneAudio(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/generate-scene-audio',
    data
  })
}

function clusterScenesWithAudio(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/cluster-scenes-with-audio',
    data
  })
}

function calculateDurations(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/calculate-durations-v2',
    data
  })
}

function getSceneTimeline(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/get-scene-timeline',
    data
  })
}

function regenerateSegment(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/regenerate-segment',
    data
  })
}

function updatePause(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/update-pause',
    data
  })
}

function updateSegmentText(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/update-segment-text',
    data
  })
}

function generateAudioSequences(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/generate-audio-sequences',
    data
  })
}

function assembleScenesWithAudio(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/assemble-scenes-with-audio-and-transitions',
    data
  })
}

function assembleScenesWithAudioAndDialogue(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/assemble-scenes-with-audio-dialogue-and-transitions',
    data
  })
}

function generateImageWithReferenceNanoBanana(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/nano-banana-image-generate-with-reference',
    data
  })
}

function generateDraftVideo(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/drafts-generation-mode-v2',
    data
  })
}

function editVideo(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/video-edit',
    data
  })
}

function getPresignedUrls(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/get-presigned-urls',
    data
  })
}

function generateNewStyle(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/generate-new-style',
    data
  })
}

function listEditingStyles(data = {}) {
  console.log('listEditingStyles called with data:', data)
  return draftVideoInstance({
    method: 'POST',
    url: '/list-editing-styles',
    data
  })
}

function deleteEditingStyles(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/delete-editing-styles',
    data
  })
}

function getGeneralPresignedUrls(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/get-presigned-urls-general',
    data
  })
}

function generateOutfit(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/generate-outfit',
    data
  })
}

function generateMusic(data) {
  return draftVideoInstance({
    method: 'POST',
    url: '/music-generation',
    data
  })
}

function getVideoAnalysis(userId, videoId) {
  return draftVideoInstance({
    method: 'GET',
    url: `/analytics/usage/${userId}/video/${videoId}`
  })
}

function checkAccess() {
  return draftVideoInstance({
    method: 'GET',
    url: '/access-ai-video'
  })
}

export {
  changeMachineState,
  generateVideo,
  generateShots,
  mapCharacterToShots,
  groupShotsToScenes,
  generateVideoStory,
  getAvailableCharacters,
  createCharacter,
  generateImageDeepAI,
  generateVideoDeepAI,
  generateTransition,
  getAiVideos,
  getAiVideoById,
  createAiVideo,
  updateAiVideo,
  deleteAiVideo,
  getTaskStatus,
  generateLocationReference,
  generatePropReference,
  generateImageCraiyon,
  editImageDeepAI,
  generateLumaVideo,
  listOutfits,
  listAIVoices,
  generateAudio,
  generateDialogue,
  uploadCustomVoice,
  generateSceneAudio,
  clusterScenesWithAudio,
  calculateDurations,
  assembleScenesWithAudio,
  getSceneTimeline,
  regenerateSegment,
  updatePause,
  updateSegmentText,
  generateAudioSequences,
  generateImageWithReferenceNanoBanana,
  generateDraftVideo,
  editVideo,
  getPresignedUrls,
  generateNewStyle,
  listEditingStyles,
  deleteEditingStyles,
  getGeneralPresignedUrls,
  generateOutfit,
  generateMusic,
  getVideoAnalysis,
  assembleScenesWithAudioAndDialogue,
  regenerateVoiceover,
  checkAccess
}
